import { useState } from "react";
import type { PaymentMethod, PickupMethod } from "@/mocks/branch/orders";
import { POS_PAYMENT_OPTIONS, type CartLine, type ToggleOption } from "../constants";

interface CartPanelProps {
  lines: CartLine[];
  orderType: PickupMethod;
  paymentMethod: PaymentMethod;
  notes: string;
  subtotal: number;
  vat: number;
  total: number;
  availableMethods: ToggleOption<PickupMethod>[];
  branchClosed: boolean;
  estimatedMinutes: number;
  emptyWarning: boolean;
  onIncrement: (productId: string) => void;
  onDecrement: (productId: string) => void;
  onRemove: (productId: string) => void;
  onClear: () => void;
  onNotesChange: (value: string) => void;
  onOrderTypeChange: (value: PickupMethod) => void;
  onPaymentChange: (value: PaymentMethod) => void;
  onConfirm: () => void;
}

export default function CartPanel({
  lines,
  orderType,
  paymentMethod,
  notes,
  subtotal,
  vat,
  total,
  availableMethods,
  branchClosed,
  estimatedMinutes,
  emptyWarning,
  onIncrement,
  onDecrement,
  onRemove,
  onClear,
  onNotesChange,
  onOrderTypeChange,
  onPaymentChange,
  onConfirm,
}: CartPanelProps) {
  const [notesOpen, setNotesOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const itemCount = lines.reduce((sum, l) => sum + l.quantity, 0);

  const handleClear = () => {
    if (confirmClear) {
      onClear();
      setConfirmClear(false);
    } else {
      setConfirmClear(true);
      window.setTimeout(() => setConfirmClear(false), 2500);
    }
  };

  const branchClosedBlock = branchClosed;
  const emptyBlock = lines.length === 0;

  return (
    <div className="flex flex-col h-full min-h-0 bg-background-50">
      <div className="flex items-center justify-between px-4 h-14 border-b border-background-200">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center text-primary-700">
            <i className="ri-shopping-cart-2-line text-lg"></i>
          </div>
          <h2 className="text-base font-bold text-foreground-900">الطلب الحالي</h2>
          {itemCount > 0 ? (
            <span
              key={itemCount}
              className="min-w-6 h-6 px-1.5 rounded-full bg-primary-500 text-white text-xs font-bold flex items-center justify-center animate-pop-check"
            >
              {itemCount}
            </span>
          ) : null}
        </div>
        {lines.length > 0 ? (
          <button
            type="button"
            onClick={handleClear}
            className={`h-9 px-3 rounded-btn text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              confirmClear ? "bg-red-500 text-white" : "text-red-600 hover:bg-red-50"
            }`}
          >
            {confirmClear ? "تأكيد التفريغ؟" : "تفريغ"}
          </button>
        ) : null}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5">
        {lines.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-16">
            <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-shopping-basket-line text-2xl text-foreground-400"></i>
            </div>
            <p className="mt-3 text-sm font-semibold text-foreground-600">السلة فارغة</p>
            <p className="mt-1 text-xs text-foreground-500">أضف منتجات من القائمة لبدء الطلب</p>
          </div>
        ) : (
          lines.map((line) => (
            <div
              key={line.productId}
              className="flex items-center gap-2.5 bg-background-100 rounded-card p-2.5 animate-fade-slide-up"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground-900 truncate">{line.name}</p>
                <p className="text-xs text-foreground-500 mt-0.5">
                  {line.unitPrice.toFixed(2)} × {line.quantity} = {(line.unitPrice * line.quantity).toFixed(2)} ر.س
                </p>
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  aria-label={`زيادة كمية ${line.name}`}
                  onClick={() => onIncrement(line.productId)}
                  className="w-10 h-10 rounded-btn bg-primary-500 text-white flex items-center justify-center hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-add-line text-lg"></i>
                </button>
                <span className="w-8 text-center text-sm font-bold text-foreground-900">{line.quantity}</span>
                <button
                  type="button"
                  aria-label={`تقليل كمية ${line.name}`}
                  onClick={() => onDecrement(line.productId)}
                  className="w-10 h-10 rounded-btn bg-background-200 text-foreground-700 flex items-center justify-center hover:bg-background-300 transition-colors cursor-pointer"
                >
                  <i className="ri-subtract-line text-lg"></i>
                </button>
              </div>

              <button
                type="button"
                aria-label={`حذف ${line.name} من السلة`}
                onClick={() => onRemove(line.productId)}
                className="w-10 h-10 rounded-btn text-red-600 flex items-center justify-center hover:bg-red-50 transition-colors cursor-pointer"
              >
                <i className="ri-delete-bin-line text-lg"></i>
              </button>
            </div>
          ))
        )}
      </div>

      <div className="px-4 py-3 border-t border-background-200 space-y-4">
        <div>
          <button
            type="button"
            onClick={() => setNotesOpen((v) => !v)}
            className="flex items-center gap-2 text-sm font-semibold text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-chat-3-line text-base"></i>
            </div>
            ملاحظات على الطلب
            <i className={`ri-arrow-down-s-line transition-transform ${notesOpen ? "rotate-180" : ""}`}></i>
          </button>
          {notesOpen ? (
            <textarea
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="أي ملاحظات خاصة؟"
              className="mt-2 w-full rounded-btn border border-background-300 bg-background-50 p-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
            />
          ) : null}
        </div>

        <div>
          <p className="text-xs font-semibold text-foreground-600 mb-2">نوع الطلب</p>
          <div className="grid grid-cols-2 gap-2">
            {availableMethods.map((opt) => {
              const active = orderType === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onOrderTypeChange(opt.value)}
                  className={`h-12 rounded-btn flex items-center justify-center gap-2 text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                    active
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-700 border border-background-300 hover:bg-background-200"
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${opt.icon} text-lg`}></i>
                  </div>
                  {opt.label}
                  {active ? <i className="ri-check-line text-lg"></i> : null}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-foreground-600 mb-2">وسيلة الدفع</p>
          <div className="grid grid-cols-2 gap-2">
            {POS_PAYMENT_OPTIONS.map((opt) => {
              const active = paymentMethod === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onPaymentChange(opt.value)}
                  className={`h-12 rounded-btn flex items-center justify-center gap-2 text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                    active
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-700 border border-background-300 hover:bg-background-200"
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${opt.icon} text-lg`}></i>
                  </div>
                  {opt.label}
                  {active ? <i className="ri-check-line text-lg"></i> : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-1.5 pt-1">
          <div className="flex items-center justify-between text-sm">
            <span className="text-foreground-600">المجموع الفرعي</span>
            <span className="font-semibold text-foreground-900">{subtotal.toFixed(2)} ر.س</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-foreground-600">ضريبة القيمة المضافة (15%)</span>
            <span className="font-semibold text-foreground-900">{vat.toFixed(2)} ر.س</span>
          </div>
          <div className="flex items-center justify-between pt-1.5 border-t border-background-200">
            <span className="text-base font-bold text-foreground-900">المجموع الكلي</span>
            <span className="text-xl font-bold text-primary-700">{total.toFixed(2)} ر.س</span>
          </div>
          <p className="flex items-center gap-1.5 text-xs text-foreground-500">
            <i className="ri-information-line text-sm"></i>
            الأسعار شاملة ضريبة القيمة المضافة
          </p>
          <div className="flex items-center gap-1.5 text-xs text-foreground-500">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-time-line"></i>
            </div>
            التجهيز التقديري: {estimatedMinutes} دقيقة
          </div>
        </div>

        {emptyWarning ? (
          <div className="flex items-center gap-2 p-2.5 rounded-btn bg-red-50 border border-red-200 text-xs font-semibold text-red-700 animate-fade-slide-down">
            <i className="ri-error-warning-line text-base"></i>
            لا يمكن تأكيد الطلب — السلة فارغة
          </div>
        ) : null}

        <button
          type="button"
          disabled={branchClosedBlock || emptyBlock}
          onClick={onConfirm}
          className="w-full h-14 rounded-btn bg-primary-500 text-white text-base font-bold flex items-center justify-center gap-2 hover:bg-primary-600 disabled:bg-background-200 disabled:text-foreground-400 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-6 h-6 flex items-center justify-center">
            <i className={branchClosedBlock ? "ri-lock-line" : "ri-check-double-line"}></i>
          </div>
          {branchClosedBlock ? "الفرع مغلق مؤقتًا" : `تأكيد الطلب — ${total.toFixed(2)} ر.س`}
        </button>
      </div>
    </div>
  );
}